import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('fr-FR');
}

function formatMontant(val) {
  if (!val && val !== 0) return '';
  const n = parseFloat(String(val).replace(',', '.'));
  if (isNaN(n)) return val;
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─── Composant Champ Éditable ────────────────────────────────────────────────
const FORMES_JURIDIQUES = [
  'SARL', 'SAS', 'SA', 'SASU', 'EURL', 'SNC', 'SCI',
  'EI / Auto-entrepreneur', 'Association', 'Collectivité',
  'Établissement public', 'GIE', 'Autre'
];

function EditableField({ label, value, field, disabled, onSave, options, required }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');

  useEffect(() => {
    setVal(value || '');
  }, [value]);

  const handleSave = async () => {
    if (required && !val.trim()) {
      alert(`Le champ « ${label} » est obligatoire, il ne peut pas être vide.`);
      return;
    }
    try {
      await onSave(field, val);
      setEditing(false);
    } catch(err) {
      alert(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleCancel = () => {
    setVal(value || '');
    setEditing(false);
  };

  return (
    <div className="detail-item">
      <div className="detail-label">{label}</div>
      <div className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {editing ? (
          <>
            {options ? (
              <select
                value={val}
                onChange={e => setVal(e.target.value)}
                style={{
                  padding: '0.5rem 0.7rem',
                  border: '2px solid #3498db',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  flex: 1,
                  outline: 'none',
                  background: '#fff'
                }}
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSave();
                  if (e.key === 'Escape') handleCancel();
                }}
              >
                <option value="">— Sélectionner —</option>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
            <input
              type="text"
              value={val}
              onChange={e => setVal(e.target.value)}
              style={{
                padding: '0.5rem 0.7rem',
                border: '2px solid #3498db',
                borderRadius: '4px',
                fontSize: '0.9rem',
                flex: 1,
                outline: 'none'
              }}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            )}
            <button
              onClick={handleSave}
              style={{
                background: '#27ae60', color: '#fff', border: 'none',
                borderRadius: '4px', padding: '0.4rem 0.7rem',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
              }}
              title="Enregistrer (Enter)"
            >✓</button>
            <button
              onClick={handleCancel}
              style={{
                background: '#e74c3c', color: '#fff', border: 'none',
                borderRadius: '4px', padding: '0.4rem 0.7rem',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
              }}
              title="Annuler (Esc)"
            >✕</button>
          </>
        ) : (
          <>
            <span style={{ flex: 1 }}>{value || '—'}</span>
            {!disabled && (
              <button
                onClick={() => setEditing(true)}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: '0.2rem 0.4rem', transition: 'all 0.2s',
                  opacity: 0.4, display: 'flex', alignItems: 'center'
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.transform = 'scale(1)'; }}
                title="Modifier ce champ"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
function ClientDetail() {
  const { codification } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDetail(); }, [codification]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDetail = async () => {
    try {
      const r = await axios.get('/api/clients/' + codification);
      setData(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleFieldSave = async (field, value) => {
    try {
      await axios.put('/api/clients/' + codification, {
        [field]: value || null
      });
      await fetchDetail();
    } catch(err) {
      throw new Error(err.response?.data?.error || 'Erreur de sauvegarde');
    }
  };

  if (loading) return <div className="loading">Chargement…</div>;
  if (!data) return <div className="error-message">Client non trouvé</div>;

  const { client, marches } = data;

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">{client.nom}</h1>
          <span style={{
            background: '#d1ecf1', color: '#0c5460',
            padding: '0.3rem 0.8rem', borderRadius: '12px',
            fontSize: '0.85rem', fontWeight: 600
          }}>
            {client.forme_juridique || 'Client'}
          </span>
        </div>

        {/* Infos générales avec édition inline */}
        <div className="detail-section">
          <h3>Informations générales</h3>
          <div className="detail-grid">
            <EditableField
              label="Codification"
              value={client.codification}
              field="codification"
              disabled={true}
              onSave={handleFieldSave}
            />
            <EditableField
              label="Nom"
              value={client.nom}
              field="nom"
              required={true}
              onSave={handleFieldSave}
            />
            <EditableField
              label="SIRET"
              value={client.siret}
              field="siret"
              onSave={handleFieldSave}
            />
            <EditableField
              label="Forme juridique"
              value={client.forme_juridique}
              field="forme_juridique"
              onSave={handleFieldSave}
              options={FORMES_JURIDIQUES}
            />
            <EditableField
              label="Adresse"
              value={client.adresse}
              field="adresse"
              onSave={handleFieldSave}
            />
            <EditableField
              label="Code postal"
              value={client.code_postal}
              field="code_postal"
              onSave={handleFieldSave}
            />
            <EditableField
              label="Ville"
              value={client.ville}
              field="ville"
              onSave={handleFieldSave}
            />
            <EditableField
              label="Représentant"
              value={client.representant}
              field="representant"
              onSave={handleFieldSave}
            />
            <EditableField
              label="Titre représentant"
              value={client.titre_representant}
              field="titre_representant"
              onSave={handleFieldSave}
            />
          </div>
        </div>

        {/* Marchés associés */}
        <div className="detail-section">
          <h3>Marchés associés ({marches?.length || 0})</h3>
          {!marches || marches.length === 0 ? (
            <p style={{ color: '#95a5a6' }}>Aucun marché associé à ce client</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Réf. interne</th>
                  <th>Réf. client</th>
                  <th>Intitulé</th>
                  <th>Lot</th>
                  <th>Montant</th>
                  <th>État</th>
                  <th>Notification</th>
                </tr>
              </thead>
              <tbody>
                {marches.map(m => (
                  <tr key={m.reference_interne}>
                    <td><strong>{m.codification || '#' + m.reference_interne}</strong></td>
                    <td>{m.reference_client || '—'}</td>
                    <td>{m.intitule_marche}</td>
                    <td>{m.lot || '—'}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {m.montant ? formatMontant(m.montant) + ' €' : '—'}
                    </td>
                    <td>
                      {(() => {
                        const etat = m.etat || (m.retenu ? 'Gagné' : 'En cours');
                        const colors = {
                          'Non répondu': { background: '#e2e3e5', color: '#383d41' },
                          'En cours':    { background: '#d1ecf1', color: '#0c5460' },
                          'Perdu':       { background: '#f8d7da', color: '#721c24' },
                          'Gagné':       { background: '#d4edda', color: '#155724' },
                          'Terminé':     { background: '#e2d5f1', color: '#4a235a' },
                          'Declaration sans suite': { background: '#fde9d0', color: '#9c4221' },
                        };
                        const style = colors[etat] || colors['En cours'];
                        return (
                          <span style={{
                            ...style, padding: '0.25rem 0.6rem', borderRadius: '12px',
                            fontSize: '0.82rem', fontWeight: 600
                          }}>
                            {etat}
                          </span>
                        );
                      })()}
                    </td>
                    <td>{m.notification ? fmtDate(m.notification) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientDetail;
